import React, {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import 'styles/chat/Chat.css';
import UniversalEmojiMenu from 'components/universal/input/UniversalEmojiMenu';
import getUnownedMessage, {allAscii, isOnlyAscii} from 'util/unownedItemMessage';
import UniversalInputField from 'components/universal/input/UniversalInputField';
import {MarketContext} from 'util/market/MarketContext';
import KeyboardIconSvg from 'util/svg/KeyboardIconSvg';
import {centsToString} from 'util/cashFormatter';
import UniversalKeyboardSizeWrapper from 'components/universal/input/UniversalKeyboardSizeWrapper';
import AddMoneySendArrow from 'components/donate/AddMoneySendArrow';

const greenArrowColor = '#266F2D';

const UniversalComplexInput = forwardRef(({
                                              sendAmount,
                                              onHeightResize = () => {
                                              },
                                              onSendMessageRequest,
                                              onInputLengthChange,
                                              allowAllLetters = false,
                                              isPictureComment = false,
                                              inputPlaceholder = 'Type a message',
                                          }, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const [bubbleMessage, setBubbleMessage] = useState('');
    const [isInputDisabled, setIsInputDisabled] = useState(false);
    const [isKeyboardRed, setIsKeyboardRed] = useState(false);
    const [inputLength, setInputLength] = useState(0);
    const [highlightedKey, setHighlightedKey] = useState(null);

    const inputContainerRef = useRef(null);
    const emojiMenuButtonRef = useRef(null);
    const chatInputFieldRef = useRef(null);
    const bubbleTimeoutRef = useRef(null);
    const highlightTimeoutRef = useRef(null); // Ref to store highlight timeout
    const colorTimeoutRef = useRef();

    const [color, setColor] = useState('black');
    const [colorTransition, setColorTransition] = useState('none');

    const {allowedLetters, allowedEmojis} = useContext(MarketContext);
    const [keyboardAllowedLetters, setKeyboardAllowedLetters] = useState('');
    const keyboardRef = useRef(null);

    useEffect(() => {
        if (allowAllLetters) {
            setKeyboardAllowedLetters(allAscii);
        } else {
            setKeyboardAllowedLetters(allowedLetters);
        }
    }, [allowAllLetters, allowedLetters]);

    // Observe input-container div resize
    useEffect(() => {
        if (inputContainerRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                if (onHeightResize) {
                    onHeightResize();
                }
            });
            resizeObserver.observe(inputContainerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [onHeightResize]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
                setKeyboardVisible(false);
            }
        };

        document.addEventListener('mouseup', handleClickOutside);

        return () => {
            document.removeEventListener('mouseup', handleClickOutside);
        };
    }, []);

    const handleInputChange = (newInputValue, removedValue) => {
        setInputValue(newInputValue);

        if (removedValue) {
            setBubbleMessage(getUnownedMessage(removedValue, keyboardAllowedLetters));
            if (isOnlyAscii(removedValue) && !keyboardVisible) {
                setIsKeyboardRed(true);
            } else {
                setIsKeyboardRed(false);
            }
            if (bubbleTimeoutRef.current) {
                clearTimeout(bubbleTimeoutRef.current);
            }
            bubbleTimeoutRef.current = setTimeout(() => {
                setBubbleMessage('');
                setIsKeyboardRed(false);
            }, 2000);
        } else {
            if (bubbleTimeoutRef.current) {
                setBubbleMessage('');
            }
            setIsKeyboardRed(false);
        }
    };

    const handleEnterPress = () => {
        handleSendMessageRequest();
    };

    const handleOutsideKeyPress = (key) => {
        chatInputFieldRef.current?.insertKey(key);
    };

    const handleSendMessageRequest = () => {
        if (inputLength === 0 || isInputDisabled || inputValue.trim() === '') {
            return;
        }

        setIsInputDisabled(true);

        onSendMessageRequest({
            inputValue,
            sendAmount,
            inputLength,
            onSuccess: () => {
                setInputValue('');
                chatInputFieldRef.current?.clearInput();
                setIsInputDisabled(false);
                emojiMenuButtonRef.current?.hideEmojiPopup();
                setInputLength(0);
                setTimeout(() => chatInputFieldRef.current?.callFocus(), 0);
            },
            onError: (error) => {
                console.error('Error sending message:', error);
                setIsInputDisabled(false);
            },
            setIsInputDisabled,
        });
    };

    const toggleKeyboard = () => {
        if (!keyboardVisible) {
            setIsKeyboardRed(false);
            setBubbleMessage('');
        }
        setKeyboardVisible(!keyboardVisible);
    };

    // Expose priceIncreased and priceDecreased methods via ref
    useImperativeHandle(ref, () => ({
        priceIncreased() {
            setColorTransition('none');
            setColor('red');
            if (colorTimeoutRef.current) {
                clearTimeout(colorTimeoutRef.current);
            }
            colorTimeoutRef.current = setTimeout(() => {
                setColorTransition('color 0.7s');
                setColor('black');
            }, 400);
        },
        priceDecreased() {
            setColorTransition('none');
            setColor('green');
            if (colorTimeoutRef.current) {
                clearTimeout(colorTimeoutRef.current);
            }
            colorTimeoutRef.current = setTimeout(() => {
                setColorTransition('color 0.7s');
                setColor('black');
            }, 400);
        },
        focus() {
            chatInputFieldRef.current?.callFocus();
        },
        blurAndClear() {
            setInputValue('');
            chatInputFieldRef.current?.clearInput();
            chatInputFieldRef.current?.blur();
        }
    }));

    useEffect(() => {
        // Whenever inputLength changes, notify parent
        if (onInputLengthChange) {
            onInputLengthChange(inputLength);
        }
    }, [inputLength]);

    const realKeyUp = (event) => {
        keyboardRef.current?.realKeyUp(event.key); // Inform UniversalKeyboard
    };

    const realKeyDown = (event) => {
        keyboardRef.current?.realKeyDown(event.key); // Inform UniversalKeyboard
    };

    const onMobileKeyPressed = (key) => {
        // Clear previous highlight timeout before starting a new one
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }

        setHighlightedKey(key.toLowerCase());
        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedKey(null);
        }, 150);
    };

    return (
        <div className={`input-container ${isPictureComment ? 'comment' : ''}`} ref={inputContainerRef}>
            <div className="input-keyboard-container">
                <div
                    className={`chat-input-wrapper ${isPictureComment ? 'comment' : ''} ${keyboardVisible ? 'visible-keyboard' : ''}`}>
                    {bubbleMessage && <div className="bubble">{bubbleMessage}</div>}
                    {!allowAllLetters && (
                        <div
                            className="keyboard-icon"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                toggleKeyboard();
                            }}
                        >
                            <KeyboardIconSvg color={keyboardVisible ? 'black' : (isKeyboardRed ? 'red' : 'gray')}/>
                        </div>
                    )}
                    <div className={`emoji-menu-button ${allowAllLetters ? 'no-keyboard' : ''}`}>
                        <UniversalEmojiMenu
                            ref={emojiMenuButtonRef}
                            onKeyPress={handleOutsideKeyPress}
                        />
                    </div>
                    <div className={`chat-input-border ${allowAllLetters ? 'no-keyboard' : ''}`}>
                        <UniversalInputField
                            ref={chatInputFieldRef}
                            allowedLetters={keyboardAllowedLetters}
                            allowedEmojis={allowedEmojis}
                            isInputDisabled={isInputDisabled}
                            onInputChange={handleInputChange}
                            onEnterPress={handleEnterPress}
                            inputPlaceholder={inputPlaceholder}
                            maxInputLength={500}
                            setInputLength={setInputLength}
                            realKeyboardKeyUp={realKeyUp}
                            realKeyboardKeyDown={realKeyDown}
                            mobileKeyPressed={onMobileKeyPressed}
                        />
                    </div>

                    <div className={`send-arrow-container ${!inputLength || isInputDisabled ? 'disabled' : ''}`}
                         onClick={handleSendMessageRequest}>
                        <AddMoneySendArrow handleSubmit={handleSendMessageRequest} isSending={isInputDisabled}
                                           isLegitAmount={inputLength} isInChat={true}/>
                        <div
                            className={`calculated-send-cost  ${!inputLength || isInputDisabled ? 'disabled' : ''}`}
                            onClick={handleSendMessageRequest}
                            style={{color: color, transition: colorTransition}}
                        >
                            {centsToString(sendAmount, true, true, true)}
                        </div>
                    </div>
                </div>
                {keyboardVisible &&
                    <UniversalKeyboardSizeWrapper
                        onKeyPress={handleOutsideKeyPress}
                        isPictureComment={isPictureComment}
                        ref={keyboardRef}
                        highlightedKey={highlightedKey}
                    />
                }
            </div>
        </div>
    );
});

export default UniversalComplexInput;
